import { MIN_SIGNATURE_TOKEN_LENGTH, STOP_WORDS } from './constants';

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Match a phrase in the original message without stripping punctuation.
 * Uses non-word-char boundaries instead of \\b so titles like "(A Class)" still match
 * before commas, periods, or spaces (\\b fails after closing parentheses).
 */
export function phraseMatchPattern(phrase: string): RegExp {
  return new RegExp(`(?<!\\w)${escapeRegex(phrase)}(?!\\w)`, 'gi');
}

export function normalizePhrase(value: string): string {
  return value.trim().replace(/\s+/g, ' ');
}

function isWordTokenValid(word: string): boolean {
  if (/^\d+$/.test(word)) return true;
  if (word.length >= 2) return true;
  // Allow single-letter class markers (e.g. "A Class") from fund titles.
  if (word.length === 1 && /^[A-Za-z]$/.test(word)) return true;
  return false;
}

export function isValidMatchPhrase(phrase: string): boolean {
  const normalized = normalizePhrase(phrase);
  if (!normalized) return false;
  if (STOP_WORDS.has(normalized.toLowerCase())) return false;

  const words = normalized.split(' ');
  if (words.length === 1) {
    return normalized.length >= 4;
  }
  if (normalized.length < 6) return false;
  return words.every(isWordTokenValid);
}

/**
 * Extra match phrases derived from a fund title (corpus only — never applied to message text).
 * e.g. "FCOF Wantirna (A Class)" → full name, "FCOF Wantirna A Class", "FCOF Wantirna", then signature tokens.
 */
export function expandFundMatchPhrases(fundName: string): string[] {
  const normalized = normalizePhrase(fundName);
  if (!normalized) return [];

  const candidates: string[] = [normalized];
  const parenToSpace = normalizePhrase(normalized.replace(/\(([^)]*)\)/g, ' $1 '));
  if (parenToSpace !== normalized) {
    candidates.push(parenToSpace);
  }
  const withoutTrailingParen = normalizePhrase(normalized.replace(/\s*\([^)]*\)\s*$/, ''));
  if (withoutTrailingParen && withoutTrailingParen !== normalized) {
    candidates.push(withoutTrailingParen);
  }
  return collectPhrases(...candidates);
}

export function phraseWordCount(phrase: string): number {
  return normalizePhrase(phrase).split(/\s+/).filter(Boolean).length;
}

export function collectPhrases(...candidates: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const phrases: string[] = [];
  for (const candidate of candidates) {
    const normalized = normalizePhrase(candidate ?? '');
    if (!normalized || !isValidMatchPhrase(normalized)) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    phrases.push(normalized);
  }
  phrases.sort((left, right) => right.length - left.length);
  return phrases;
}

/** Fund-series prefixes (FCOF, FCOH, …) — too ambiguous alone; keep full names like "FCOF Wantirna". */
export function isFundFamilyAcronym(token: string): boolean {
  return (
    token.length >= MIN_SIGNATURE_TOKEN_LENGTH && token.length <= 5 && /^[A-Z0-9]+$/.test(token)
  );
}

export { isWordTokenValid };
